import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, User, Grid3x3, List, Mail, Phone, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Building2, ChevronUp, ChevronDown, ChevronsUpDown, Network, Sun, Moon, LogOut, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import SegmentedControl from '../components/SegmentedControl';
import MailingLists from '../components/MailingLists';
import UsersManagement from '../components/UsersManagement';
import SyncAdministration from '../components/SyncAdministration';
import AppLayout from '../components/AppLayout';
import MaintenanceBanner from '../components/MaintenanceBanner';
import MaintenanceModal from '../components/MaintenanceModal';
import { API_URL } from '../apiBase';

const getPhotoUrl = (photo_url) => {
  if (!photo_url) return null;
  // Si c'est du base64, retourner tel quel
  if (photo_url.startsWith('data:')) return photo_url;
  // Si c'est une URL externe, retourner tel quel
  if (photo_url.startsWith('http')) return photo_url;
  // Sinon, ajouter l'URL du backend
  return `${API_URL}${photo_url}`;
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // If starts with 33, format as +33 x xx xx xx xx
  if (cleaned.startsWith('33') && cleaned.length === 11) {
    return `+33 ${cleaned[2]} ${cleaned.substring(3, 5)} ${cleaned.substring(5, 7)} ${cleaned.substring(7, 9)} ${cleaned.substring(9, 11)}`;
  }
  
  // If starts with 0 and has 10 digits, convert to +33 format
  if (cleaned.startsWith('0') && cleaned.length === 10) {
    return `+33 ${cleaned[1]} ${cleaned.substring(2, 4)} ${cleaned.substring(4, 6)} ${cleaned.substring(6, 8)} ${cleaned.substring(8, 10)}`;
  }
  
  // Return original if doesn't match expected format
  return phone;
};

const getInitials = (nom, prenom) => {
  const n = nom?.charAt(0) || '';
  const p = prenom?.charAt(0) || '';
  return `${p}${n}`.toUpperCase();
};

const DashboardPage = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [selectedService, setSelectedService] = useState('all');
  const [selectedLetter, setSelectedLetter] = useState('all');
  const [viewMode, setViewMode] = useState(() => {
    // Load saved preference from localStorage
    return localStorage.getItem('directory-view-mode') || 'cards';
  });
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 15;
  const [currentUser, setCurrentUser] = useState(null);
  
  // Maintenance state
  const [maintenanceStatus, setMaintenanceStatus] = useState({ is_active: false });
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState('nom'); // Default sort by name
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  // Mode toggle: annuaire or listes
  const [displayMode, setDisplayMode] = useState(() => {
    return localStorage.getItem('display-mode') || 'annuaire';
  });
  
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Handle navigation state to set active tab
  useEffect(() => {
    if (location.state?.activeTab) {
      if (location.state.activeTab === 'grid') setDisplayMode('annuaire');
      if (location.state.activeTab === 'lists') setDisplayMode('listes');
      if (location.state.activeTab === 'users') setDisplayMode('utilisateurs');
      if (location.state.activeTab === 'sync') setDisplayMode('synchronisation');
    }
  }, [location.state]);
  
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const fetchData = async () => {
    try {
      const [usersRes, companiesRes, servicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/users`),
        axios.get(`${API_URL}/api/companies`),
        axios.get(`${API_URL}/api/services`)
      ]);
      
      setUsers(usersRes.data);
      setCompanies(companiesRes.data.companies);
      setServices(servicesRes.data.services);
      setLoading(false);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      let url = `${API_URL}/api/services`;
      if (selectedCompany && selectedCompany !== 'all') {
        url += `?societe=${encodeURIComponent(selectedCompany)}`;
      }
      const response = await axios.get(url);
      setServices(response.data.services);
      // Reset service selection when company changes
      setSelectedService('all');
    } catch (error) {
      console.error('Erreur lors du chargement des services', error);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((user) => {
        const fields = [
          user.nom,
          user.prenom,
          user.email,
          user.email_principal,
          user.email_secondaire,
          user.poste,
          user.position,
        ];
        return fields.some(
          (v) => v != null && String(v).toLowerCase().includes(term)
        );
      });
    }
    
    if (selectedCompany && selectedCompany !== 'all') {
      filtered = filtered.filter(user => user.societe === selectedCompany);
    }
    
    if (selectedService && selectedService !== 'all') {
      filtered = filtered.filter(user => user.service === selectedService);
    }
    
    if (selectedLetter && selectedLetter !== 'all') {
      filtered = filtered.filter(user => 
        user.nom?.charAt(0).toUpperCase() === selectedLetter
      );
    }
    
    // Apply sorting
    filtered = sortUsers(filtered);
    
    setFilteredUsers(filtered);
  };
  
  // Sorting function
  const sortUsers = (usersToSort) => {
    return [...usersToSort].sort((a, b) => {
      let valueA, valueB;
      
      switch(sortColumn) {
        case 'nom':
          valueA = `${a.prenom || ''} ${a.nom || ''}`.toLowerCase();
          valueB = `${b.prenom || ''} ${b.nom || ''}`.toLowerCase();
          break;
        case 'poste':
          valueA = (a.poste || '').toLowerCase();
          valueB = (b.poste || '').toLowerCase();
          break;
        case 'societe':
          valueA = (a.societe || '').toLowerCase();
          valueB = (b.societe || '').toLowerCase();
          break;
        case 'service':
          valueA = (a.service || '').toLowerCase();
          valueB = (b.service || '').toLowerCase();
          break;
        case 'contact':
          // Sort by email for contact column
          valueA = (a.email || '').toLowerCase();
          valueB = (b.email || '').toLowerCase();
          break;
        default:
          valueA = (a.nom || '').toLowerCase();
          valueB = (b.nom || '').toLowerCase();
      }
      
      const comparison = valueA.localeCompare(valueB);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };
  
  // Handle sort column change
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  
  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('directory-view-mode', mode);
  };
  
  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);
  
  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (nom, prenom) => {
    const n = nom?.charAt(0) || '';
    const p = prenom?.charAt(0) || '';
    return `${n}${p}`.toUpperCase();
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Get current user info from the auth context or fetch from backend
    if (user) {
      setCurrentUser(user);
    }
  }, [user]);

  // Reset displayMode if user is not admin and tries to access admin-only views
  useEffect(() => {
    if (!currentUser?.is_admin && (displayMode === 'utilisateurs' || displayMode === 'synchronisation')) {
      setDisplayMode('annuaire');
      localStorage.setItem('display-mode', 'annuaire');
    }
  }, [currentUser, displayMode]);

  useEffect(() => {
    filterUsers();
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [users, searchTerm, selectedCompany, selectedService, selectedLetter, sortColumn, sortDirection]);

  useEffect(() => {
    // Reload services when company changes
    fetchServices();
  }, [selectedCompany]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <AppLayout>

      {/* Main Content */}
      <main
        className={
          displayMode === 'synchronisation'
            ? 'w-full py-8 pl-4 pr-6'
            : 'container mx-auto px-6 py-8'
        }
      >
        {/* Search & Filters - Only show for Annuaire mode */}
        {displayMode === 'annuaire' && (
          <div className="mb-8 bg-card border border-border rounded-md p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="search-input"
                  placeholder="Rechercher par nom, prénom, emails ou poste..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger className="w-full md:w-48" data-testid="company-filter">
                <SelectValue placeholder="Société" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les sociétés</SelectItem>
                {companies.map(company => (
                  <SelectItem key={company} value={company}>{company}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedService} onValueChange={setSelectedService}>
              <SelectTrigger className="w-full md:w-48" data-testid="service-filter">
                <SelectValue placeholder="Secteur" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les secteurs</SelectItem>
                {services.map(service => (
                  <SelectItem key={service} value={service}>{service}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Alphabetical Filter */}
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setSelectedLetter('all')}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  selectedLetter === 'all' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'hover:bg-accent'
                }`}
              >
                Tous
              </button>
              {alphabet.map(letter => (
                <button
                  key={letter}
                  onClick={() => setSelectedLetter(letter)}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    selectedLetter === letter 
                      ? 'bg-primary text-primary-foreground' 
                      : 'hover:bg-accent'
                  }`}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>
          
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''} trouvé{filteredUsers.length > 1 ? 's' : ''}
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 bg-muted rounded-md p-1">
              <button
                onClick={() => toggleViewMode('cards')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'cards' 
                    ? 'bg-background shadow-sm' 
                    : 'hover:bg-background/50'
                }`}
                title="Vue cartes"
              >
                <Grid3x3 className="h-4 w-4" />
              </button>
              <button
                onClick={() => toggleViewMode('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-background shadow-sm' 
                    : 'hover:bg-background/50'
                }`}
                title="Vue liste"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
        )}

        {/* Content based on display mode */}
        {displayMode === 'listes' ? (
          // Mailing Lists View
          <MailingLists />
        ) : displayMode === 'utilisateurs' ? (
          // Users Management View (Admin Only)
          <UsersManagement usersSubTab={location.state?.usersSubTab || 'users'} />
        ) : displayMode === 'synchronisation' ? (
          <SyncAdministration
            syncSubTab={(() => {
              const tab = location.state?.syncSubTab || 'config';
              return tab === 'mail' || tab === 'entra' ? 'config' : tab;
            })()}
          />
        ) : (
          // Annuaire View
          <>
        {/* Users Display - Cards or List */}
        {viewMode === 'cards' ? (
          // Cards View
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedUsers.map(user => (
              <div
                key={user.id}
                onClick={() => navigate(`/user/${user.id}`)}
                className="bg-card border border-border rounded-md p-6 hover:-translate-y-1 hover:border-foreground/20 transition-all cursor-pointer"
                data-testid={`user-card-${user.id}`}
              >
                <div className="flex items-start gap-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={getPhotoUrl(user.photo_url)} />
                    <AvatarFallback>{getInitials(user.nom, user.prenom)}</AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {user.prenom} {user.nom}
                    </h3>
                    <p className="text-sm text-muted-foreground truncate" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {user.email}
                    </p>
                    <div className="mt-2 space-y-1">
                      {user.telephone && (
                        <p className="text-xs text-muted-foreground">{formatPhoneNumber(user.telephone)}</p>
                      )}
                      {user.poste && (
                        <p className="text-xs font-medium">{user.poste}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                  <div className="text-xs">
                    {user.societe && (
                      <span className="inline-block px-2 py-1 rounded bg-primary/10 text-primary font-medium">
                        {user.societe}
                      </span>
                    )}
                  </div>
                  {user.service && (
                    <div className="text-xs text-muted-foreground">
                      {user.service}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // List View
          <div className="bg-card border border-border rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th 
                      className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors select-none"
                      onClick={() => handleSort('nom')}
                    >
                      <div className="flex items-center gap-2">
                        <span>Utilisateur</span>
                        {sortColumn === 'nom' ? (
                          sortDirection === 'asc' ? 
                            <ChevronUp className="h-4 w-4" /> : 
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronsUpDown className="h-4 w-4 opacity-30" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors select-none"
                      onClick={() => handleSort('poste')}
                    >
                      <div className="flex items-center gap-2">
                        <span>Poste</span>
                        {sortColumn === 'poste' ? (
                          sortDirection === 'asc' ? 
                            <ChevronUp className="h-4 w-4" /> : 
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronsUpDown className="h-4 w-4 opacity-30" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors select-none"
                      onClick={() => handleSort('contact')}
                    >
                      <div className="flex items-center gap-2">
                        <span>Contact</span>
                        {sortColumn === 'contact' ? (
                          sortDirection === 'asc' ? 
                            <ChevronUp className="h-4 w-4" /> : 
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronsUpDown className="h-4 w-4 opacity-30" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors select-none"
                      onClick={() => handleSort('societe')}
                    >
                      <div className="flex items-center gap-2">
                        <span>Société</span>
                        {sortColumn === 'societe' ? (
                          sortDirection === 'asc' ? 
                            <ChevronUp className="h-4 w-4" /> : 
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronsUpDown className="h-4 w-4 opacity-30" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors select-none"
                      onClick={() => handleSort('service')}
                    >
                      <div className="flex items-center gap-2">
                        <span>Service</span>
                        {sortColumn === 'service' ? (
                          sortDirection === 'asc' ? 
                            <ChevronUp className="h-4 w-4" /> : 
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronsUpDown className="h-4 w-4 opacity-30" />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {paginatedUsers.map(user => (
                    <tr
                      key={user.id}
                      onClick={() => navigate(`/user/${user.id}`)}
                      className="hover:bg-muted/50 cursor-pointer transition-colors"
                      data-testid={`user-row-${user.id}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={getPhotoUrl(user.photo_url)} />
                            <AvatarFallback>{getInitials(user.nom, user.prenom)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                              {user.prenom} {user.nom}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">{user.poste || '-'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <a
                            href={`mailto:${user.email}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                            style={{ fontFamily: 'JetBrains Mono, monospace' }}
                          >
                            <Mail className="h-3 w-3" />
                            {user.email}
                          </a>
                          {user.telephone && (
                            <a
                              href={`tel:${user.telephone}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                            >
                              <Phone className="h-3 w-3" />
                              {formatPhoneNumber(user.telephone)}
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.societe && (
                          <span className="inline-block px-2 py-1 rounded bg-primary/10 text-primary font-medium text-xs">
                            {user.societe}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-muted-foreground">{user.service || '-'}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination Controls */}
        {filteredUsers.length > 0 && totalPages > 1 && (
          <div className="mt-8 flex items-center justify-between bg-card border border-border rounded-md p-4">
            <div className="text-sm text-muted-foreground">
              Affichage de {startIndex + 1} à {Math.min(endIndex, filteredUsers.length)} sur {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''}
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                title="Première page"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                title="Page précédente"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <div className="px-4 py-2 text-sm">
                Page <span className="font-medium">{currentPage}</span> sur <span className="font-medium">{totalPages}</span>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                title="Page suivante"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                title="Dernière page"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Aucun utilisateur trouvé</p>
          </div>
        )}
        </>
        )}
      </main>
    </AppLayout>
  );
};

export default DashboardPage;